/**
 * Auth + base-path resolution for the legacy `tara/mcp/*` API family.
 *
 * WHY THIS EXISTS
 * ---------------
 * Every call in `src/lib/api/mcp/*` was hard-wired to the SELLER JWT:
 *
 *   Authorization: `${accessToken.type} ${accessToken.sellerToken}`
 *
 * An invite-only agency employee has no seller account, so
 * `POST /api/v1/agency/employees/login` returns `access_token: null` by design
 * (see central-api `EmployeeAuthController::sessionPayload`). For those
 * sessions `sellerToken` is `undefined` and the header went out as the literal
 * string `"Bearer undefined"` → central-api's seller `auth` guard answered 401
 * → `asyncHandler` treated that as session death, ran `localStorageHardReset()`
 * and toasted "Token has expired. Please login again.".
 *
 * Net effect on live: merely OPENING the Integrations Hub logged out every
 * invite-only employee, because `ControlCenter`'s stat-tile effect calls two of
 * these endpoints on mount.
 *
 * THE FIX
 * -------
 * central-api already exposes the same controllers on an employee-authenticated
 * surface under an `employee/` prefix (the "W2 route migration" group in
 * `Modules/TaraAi/Routes/api/v1/api_v1.php`), gated on
 * `employee_auth` + `require_role:owner,team_lead,manager`:
 *
 *   employee/tara/mcp/index
 *   employee/tara/mcp/{shop}/credentials[/{id}|/active]
 *   employee/tara/mcp/oauth/url
 *   employee/tara/mcp/{shop}/oauth/save
 *   employee/tara/mcp/accounts/{id}
 *   employee/shops/{shop}/mcp/request-integration
 *
 * So the resolution rule is simply: use the seller JWT against the legacy paths
 * when there IS one (unchanged behaviour for sellers, white-label sellers and
 * bridged owners), otherwise use the agency `emp_` key against the `employee/`
 * paths. No backend change is required.
 */
import store from '../../../store/store';
import { CENTRAL_API_BASE_URL } from '../../constants/central-api';

/** Path prefix the employee-authenticated mirrors live under. */
const EMPLOYEE_PREFIX = 'employee/';

export interface McpAuthContext {
  /** Ready-to-send `Authorization` header value. */
  authorization: string;
  /**
   * Base URL every MCP path should be appended to — `CENTRAL_API_BASE_URL` for
   * a seller session, the same plus `employee/` for an agency-only session.
   */
  base: string;
  /** True when we resolved to the agency key + `employee/` surface. */
  isEmployeeSurface: boolean;
  /**
   * False when neither credential is present. Callers pass this to
   * `asyncHandler`'s `ignore401ForceLogOut` so a request we already know is
   * unauthenticated can never trigger the global session wipe.
   */
  hasCredential: boolean;
}

/**
 * Resolve which credential + surface this session should use for `tara/mcp/*`.
 *
 * Seller JWT wins when present: white-label sellers, plain sellers and owners
 * bridged from a `users` row all keep the exact request they sent before.
 */
export function resolveMcpAuth(): McpAuthContext {
  const state = store.getState();
  const accessToken = state.auth.authorization.accessToken;
  const isWhiteLabel = state.UI?.isWhiteLabel;

  const sellerToken: string | undefined = isWhiteLabel
    ? accessToken?.currentWhiteLabelSellerToken
    : accessToken?.sellerToken;

  if (sellerToken) {
    return {
      authorization: `${accessToken.type} ${sellerToken}`,
      base: CENTRAL_API_BASE_URL,
      isEmployeeSurface: false,
      hasCredential: true,
    };
  }

  // Agency-only session (invite-only employee). The `emp_` key lives in Redux
  // ONLY — never localStorage — per the agency-portal convention.
  const agencyKey: string | undefined = state.auth.agency?.accessToken;

  if (agencyKey) {
    return {
      authorization: `Bearer ${agencyKey}`,
      base: `${CENTRAL_API_BASE_URL}${EMPLOYEE_PREFIX}`,
      isEmployeeSurface: true,
      hasCredential: true,
    };
  }

  // Neither credential: keep the legacy shape so nothing downstream changes,
  // but flag it so the caller suppresses the 401 force-logout.
  return {
    authorization: `${accessToken?.type ?? 'Bearer'} ${sellerToken ?? ''}`.trim(),
    base: CENTRAL_API_BASE_URL,
    isEmployeeSurface: false,
    hasCredential: false,
  };
}

// ---------------------------------------------------------------------------
// Path builders — relative to the resolved base, so the same call site works on
// both surfaces. These deliberately mirror the constants in
// `lib/constants/central-api.ts`; those stay as-is for any non-MCP consumer.
// ---------------------------------------------------------------------------

/** `…/tara/mcp/index` — the available-integrations catalog. */
export function mcpServersUrl(auth: McpAuthContext): string {
  return `${auth.base}tara/mcp/index`;
}

/** `…/tara/mcp/{shopSlug}/credentials` — the shop's connections collection. */
export function mcpCredentialsUrl(
  auth: McpAuthContext,
  shopSlug: string
): string {
  return `${auth.base}tara/mcp/${shopSlug}/credentials`;
}

/** `…/tara/mcp/{shopSlug}/oauth/save`. */
export function mcpOauthSaveUrl(
  auth: McpAuthContext,
  shopSlug: string
): string {
  return `${auth.base}tara/mcp/${shopSlug}/oauth/save`;
}

/** `…/tara/mcp/oauth/url` — OAuth redirect URL for one integration slug. */
export function mcpOauthUrl(auth: McpAuthContext, mcpSlug: string): string {
  return `${auth.base}tara/mcp/oauth/url?mcp_slug=${encodeURIComponent(mcpSlug)}`;
}

/** `…/tara/mcp/accounts/{connectionId}` — re-fetch a connection's account list. */
export function mcpAccountsUrl(
  auth: McpAuthContext,
  connectionId: number
): string {
  return `${auth.base}tara/mcp/accounts/${connectionId}`;
}

/** `…/shops/{shopSlug}/mcp/request-integration`. */
export function mcpRequestIntegrationUrl(
  auth: McpAuthContext,
  shopSlug: string
): string {
  return `${auth.base}shops/${shopSlug}/mcp/request-integration`;
}
